import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Destination } from "Constant/destination";

import {
    createDestination,
    savePinterestPixels,
} from "helpers/destinations/createDestination";
import {
    getMicrosoftBingConversions,
    postMicrosoftBingMappings,
    postSaveMicrosoftBing,
} from "helpers/destinations/microsoftBing";
import {
    getLinkedinSignalConversions,
    postLinkedinSignalMappings,
    postSaveLinkedinSignal,
} from "helpers/Sources/linkedin";

import { isGoogleAdsConversionCode } from "components/organisms/Destinations/GoogleAdsConversion/GoogleAdsConversion.helpers";
import SwalAlertComp from "components/SwalAlert";

import {
    GOOGLE_ENHANCED_TYPES,
    PINTEREST_TYPES,
    PIXEL_ONLY_CREATE_TYPES,
    PIXEL_TOKEN_CREATE_TYPES,
    SAVE_DISABLED_ON_SELECT_TYPES,
    TWITTER_TYPES,
} from "../DestinationCreation.constants";
import {
    cannotBeBlank,
    getAvailableDestinationCodes,
    getCreatedDestinations,
    getDestinationTypeOptions,
} from "../DestinationCreation.helpers";
import useConversionMappingDestination from "./useConversionMappingDestination";
import useCustomAudienceDestination from "./useCustomAudienceDestination";
import useDestinationForm from "./useDestinationForm";
import useFacebookDestination from "./useFacebookDestination";
import useGoogleAdsConversionDestination from "./useGoogleAdsConversionDestination";
import useGoogleEnhancedDestination from "./useGoogleEnhancedDestination";
import useMappingOnlyDestination from "./useMappingOnlyDestination";
import useOpenAiDestination from "./useOpenAiDestination";
import usePinterestDestination from "./usePinterestDestination";
import useRedditDestination from "./useRedditDestination";
import useTiktokDestination from "./useTiktokDestination";
import useTwitterDestination from "./useTwitterDestination";
import useYandexMetricaDestination from "./useYandexMetricaDestination";

const noop = () => {};

const LINKEDIN_CONFIG = {
    code: Destination.licapi,
    readyFields: [
        "selectedAdAccountName",
        "selectedAdAccountReference",
        "partnerInstallID",
    ],
    requiredFields: ["selectedAdAccountReference", "partnerInstallID"],
    saveDestination: (data) =>
        postSaveLinkedinSignal({
            ad_account: data?.selectedAdAccountReference,
            partner_install_id: data?.partnerInstallID,
        }),
    getDestinationId: (response) => response.data?.id,
    getConversions: getLinkedinSignalConversions,
    postMappings: postLinkedinSignalMappings,
};

const MICROSOFT_BING_CONFIG = {
    code: Destination.msoffline,
    readyFields: [
        "selectedAccountName",
        "selectedAccountId",
        "partnerInstallID",
    ],
    requiredFields: ["selectedAccountId", "partnerInstallID"],
    saveDestination: (data) =>
        postSaveMicrosoftBing({
            account_id: data?.selectedAccountId,
            partner_install_id: data?.partnerInstallID,
        }),
    getDestinationId: (response) =>
        response.data?.id ?? response.data?.destination_id,
    getConversions: getMicrosoftBingConversions,
    postMappings: postMicrosoftBingMappings,
};

/**
 * Host-agnostic destination creation state machine.
 *
 * @param {object} params
 * @param {object} params.language
 * @param {Array} params.destinationsTypes Raw destination types from the store.
 * @param {Array} params.sourcesTypes Raw source types; filter the available destinations.
 * @param {object} [params.tracker] Single tracker (only read when `hostActions.trackerId` is set).
 * @param {Array} params.bussiness Dashboard businesses (FBE setup).
 * @param {object} params.appObject Facebook app id / version.
 * @param {boolean} params.isTrackerLoading
 * @param {string[]} [params.sourceTypeCodes] Source type codes to check destination
 *   compatibility against when there is no tracker yet (the wizard).
 * @param {string} [params.initialDestinationType] Destination type code selected on mount.
 * @param {boolean} [params.isTypeLocked] Prevents changing `initialDestinationType`.
 * @param {(destinationIds: any) => Promise<void>} params.onCreated
 *   Called with the created destination id(s) once creation (and any mapping) is done.
 * @param {() => void} params.onCancel Closes the host (refreshing the list when needed).
 * @param {{ trackerId?: any, getTracker?: Function, refresh: Function, refreshDestinations: Function, dismiss: Function }} params.hostActions
 *   `refresh` reloads the tracker (or the destinations list); `dismiss` closes without refreshing.
 */
const useDestinationCreation = ({
    language,
    destinationsTypes,
    sourcesTypes,
    tracker,
    bussiness,
    appObject,
    isTrackerLoading,
    sourceTypeCodes,
    initialDestinationType,
    isTypeLocked = false,
    onCreated = noop,
    onCancel = noop,
    hostActions,
}) => {
    const actions = useMemo(
        () => ({ ...hostActions, close: onCancel }),
        [hostActions, onCancel],
    );
    const { trackerId } = actions;

    const [isSaveDisabled, setSaveDisabled] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [isButtonLoader, setIsButtonLoader] = useState(false);
    // Shared by the TikTok and X OAuth flows.
    const [oauthData, setOauthData] = useState();
    const saveBusyRef = useRef(false);
    const submitButtonRef = useRef(null);

    /**
     * Primary footer actions registered by child forms. Per-type slots
     * (`${code}:mapping` wins over `${code}:form`) so the always-mounted
     * hooks do not clear each other. BigQuery uses the plain `"form"` slot
     * and unmounts with its step.
     */
    const [footerActions, setFooterActions] = useState({});
    const registerFooterAction = useCallback((key, action) => {
        setFooterActions((prev) => {
            if (prev[key] === action) return prev;
            if (action === null && !(key in prev)) return prev;
            const next = { ...prev };
            if (action === null) delete next[key];
            else next[key] = action;
            return next;
        });
    }, []);

    const setIsSaveAvailable = useCallback((isAvailable) => {
        setSaveDisabled(!isAvailable);
    }, []);

    const form = useDestinationForm({ language, setSaveDisabled });
    const { destinationCode, credentials, createPixelStatus } = form;
    const footerAction =
        (destinationCode &&
            (footerActions[`${destinationCode}:mapping`] ??
                footerActions[`${destinationCode}:form`])) ??
        footerActions.mapping ??
        footerActions.form ??
        null;

    const pinterest = usePinterestDestination({
        setSaveDisabled,
        setPixelIdError: form.setPixelIdError,
        setAccessTokenError: form.setAccessTokenError,
    });

    const completeCreation = async (destinationIds) => {
        if (trackerId) setModalLoading(true);
        try {
            await onCreated(destinationIds);
        } finally {
            setModalLoading(false);
        }
    };

    const createDestinationAction = async (response) => {
        if (!response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message,
                icon: "error",
            });
            setModalLoading(false);
            return;
        }

        const createdDestinations = getCreatedDestinations(response);
        if (!createdDestinations?.length) return;

        SwalAlertComp({
            title: language?.delete_swal_modal?.success ?? "Success",
            message: "Destination created successfully",
            icon: "success",
            timer: 3000,
        });
        setModalLoading(true);
        form.resetCredentials();
        await completeCreation(createdDestinations.map((item) => item.id));
        form.reset();
        pinterest.resetFormData();
        setModalLoading(false);
    };

    const createDestinationAsync = async (payload) => {
        const response = await createDestination(payload);
        await createDestinationAction(response);
    };

    const sharedFlowParams = {
        language,
        destinationCode,
        setSaveDisabled,
        setModalLoading,
        completeCreation,
        hostActions: actions,
        registerFooterAction,
    };

    const facebook = useFacebookDestination({
        language,
        destinationCode,
        bussiness,
        createPixelStatus,
        resetForm: form.reset,
        completeCreation,
        hostActions: actions,
    });
    const reddit = useRedditDestination();
    const tiktok = useTiktokDestination({
        language,
        destinationCode,
        createPixelStatus,
        setSaveDisabled,
        setModalLoading,
        createDestinationAction,
    });
    const twitter = useTwitterDestination({
        destinationCode,
        oauthData,
        setSaveDisabled,
    });
    const customAudience = useCustomAudienceDestination({
        setIsButtonLoader,
        createDestinationAction,
    });
    const googleEnhanced = useGoogleEnhancedDestination({
        ...sharedFlowParams,
        resetForm: form.reset,
        isSaveDisabled,
    });
    const linkedin = useConversionMappingDestination({
        ...LINKEDIN_CONFIG,
        ...sharedFlowParams,
    });
    const microsoftBing = useConversionMappingDestination({
        ...MICROSOFT_BING_CONFIG,
        ...sharedFlowParams,
    });
    const yandexMetrica = useYandexMetricaDestination(sharedFlowParams);
    const googleAds = useGoogleAdsConversionDestination(sharedFlowParams);
    const openAi = useOpenAiDestination({ ...sharedFlowParams, form });
    const mappingOnly = useMappingOnlyDestination({
        ...sharedFlowParams,
        resetForm: form.reset,
    });

    const typeOptions = useMemo(
        () => getDestinationTypeOptions(destinationsTypes),
        [destinationsTypes],
    );

    const availableDestinationCodes = useMemo(
        () =>
            getAvailableDestinationCodes({
                sourcesTypes,
                tracker,
                trackerId,
                sourceTypeCodes,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sourcesTypes, tracker?.tracker?.sources, trackerId, sourceTypeCodes],
    );

    const isTypeSelectLoading = !typeOptions.length || !sourcesTypes?.length;

    const isAnyMappingOpen =
        linkedin.isMappingOpen ||
        microsoftBing.isMappingOpen ||
        googleAds.isMappingOpen ||
        openAi.isMappingOpen;

    const handleTypeChange = (event, selectedOption) => {
        const code = selectedOption?.value;

        pinterest.resetOnTypeChange();
        form.selectDestinationCode(code);
        form.clearFieldErrors();
        setIsButtonLoader(false);
        tiktok.resetOnTypeChange();
        form.handlePixelChange({ target: { value: "" } });
        mappingOnly.resetOnTypeChange();
        linkedin.resetOnTypeChange();
        microsoftBing.resetOnTypeChange();
        yandexMetrica.resetOnTypeChange();
        googleAds.resetOnTypeChange();
        googleEnhanced.resetOnTypeChange();

        if (
            SAVE_DISABLED_ON_SELECT_TYPES.includes(code) ||
            isGoogleAdsConversionCode(code)
        ) {
            setSaveDisabled(true);
        } else if (code === Destination.sstask) {
            setSaveDisabled(false);
        } else {
            setSaveDisabled(
                credentials?.pixel_id === "" || credentials?.token === "",
            );
        }
    };

    /**
     * Clears the selected type and every per-type form state. Used when the
     * host steps back to the type catalog so a re-entered form starts clean.
     */
    const resetFormState = () => {
        handleTypeChange(null, { value: undefined });
        form.resetAll();
        reddit.reset();
        twitter.reset();
        facebook.reset();
        customAudience.reset();
        openAi.resetOnTypeChange();
        setOauthData(undefined);
        setSaveDisabled(false);
        setModalLoading(false);
        setIsButtonLoader(false);
        setFooterActions({});
        saveBusyRef.current = false;
    };

    const hasAppliedInitialTypeRef = useRef(false);
    useEffect(() => {
        if (
            !initialDestinationType ||
            hasAppliedInitialTypeRef.current ||
            !typeOptions.length
        ) {
            return;
        }
        hasAppliedInitialTypeRef.current = true;
        handleTypeChange(null, { value: initialDestinationType });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDestinationType, typeOptions]);

    const createPinterestCapi = async () => {
        setIsButtonLoader(true);
        try {
            const response = await savePinterestPixels(pinterest.capiData);
            await createDestinationAction(response);
        } finally {
            setIsButtonLoader(false);
        }
    };

    const showBlankErrors = ({ pixelId = false, token = false }) => {
        const blankMessage = cannotBeBlank(language);
        if (pixelId) form.setPixelIdError(blankMessage);
        if (token) form.setAccessTokenError(blankMessage);
        setModalLoading(false);
    };

    const handleCreateDestination = async () => {
        setModalLoading(true);
        if (!form.validateDestinationCode()) {
            setModalLoading(false);
            return;
        }

        const { pixel_id, token, app_id } = credentials;
        const isPinterest = PINTEREST_TYPES.includes(destinationCode);

        if (PIXEL_ONLY_CREATE_TYPES.includes(destinationCode)) {
            if (!pixel_id) {
                showBlankErrors({ pixelId: true });
                return;
            }
            await createDestinationAsync({
                pixel_id,
                destination_type: destinationCode,
                create_pixel: createPixelStatus,
            });
        } else if (isPinterest && !pinterest.isAuthorised) {
            if (destinationCode === Destination.pncapi) {
                const { accountId, accessToken, pinterestTagId } =
                    pinterest.formData;
                if (!accountId || !accessToken) {
                    showBlankErrors({ pixelId: true, token: true });
                    return;
                }
                await createDestinationAsync({
                    pixel_id: pinterestTagId,
                    token: accessToken,
                    destination_type: Destination.pncapi,
                    create_pixel: createPixelStatus,
                    account_id: accountId,
                });
            } else {
                await createDestinationAsync({
                    pixel_id,
                    destination_type: Destination.pnpixel,
                });
            }
        } else if (TWITTER_TYPES.includes(destinationCode)) {
            await createDestinationAsync({
                ...twitter.getCreatePayload(),
                destination_type: destinationCode,
                create_pixel: createPixelStatus,
            });
        } else if (PIXEL_TOKEN_CREATE_TYPES.includes(destinationCode)) {
            await createDestinationAsync({
                pixel_id,
                token,
                destination_type: destinationCode,
                create_pixel: createPixelStatus,
            });
        } else if (destinationCode === Destination.rdcapi) {
            if (reddit.selectedAdvertiser) {
                await createDestinationAsync({
                    pixel_id: reddit.selectedAdvertiser,
                    partner_install_id: reddit.partnerInstallId,
                    destination_type: destinationCode,
                    create_pixel: createPixelStatus,
                });
            } else if (pixel_id && token) {
                await createDestinationAsync({
                    pixel_id,
                    token,
                    destination_type: destinationCode,
                    create_pixel: createPixelStatus,
                });
            } else {
                showBlankErrors({ pixelId: true, token: true });
            }
        } else if (destinationCode === Destination.fbca) {
            setModalLoading(false);
            await customAudience.createMeta();
        } else if (destinationCode === Destination.ttca) {
            setModalLoading(false);
            await customAudience.createTiktok();
        } else if (isPinterest && pinterest.capiData) {
            setModalLoading(false);
            await createPinterestCapi();
        } else if (destinationCode === Destination.ggads4) {
            await createDestinationAsync({
                pixel_id: form.googleTagId,
                destination_type: destinationCode,
            });
        } else if (destinationCode === Destination.oaipixel) {
            if (!pixel_id) {
                showBlankErrors({ pixelId: true });
                return;
            }
            await createDestinationAsync({
                pixel_id,
                destination_type: Destination.oaipixel,
            });
        } else if (destinationCode === Destination.oaicapi) {
            await openAi.create();
        } else if (destinationCode === Destination.ttapp) {
            if (!app_id) {
                form.setAppIdError(cannotBeBlank(language));
                setModalLoading(false);
                return;
            }
            await createDestinationAsync({
                pixel_id,
                token,
                tt_app_id: app_id,
                destination_type: destinationCode,
                create_pixel: createPixelStatus,
            });
        } else {
            if (!pixel_id || !token) {
                showBlankErrors({ pixelId: true, token: true });
                return;
            }
            await createDestinationAsync({
                pixel_id,
                token,
                destination_type: destinationCode,
                create_pixel: createPixelStatus,
            });
        }
    };

    const handleSave = async () => {
        if (saveBusyRef.current) return;
        saveBusyRef.current = true;
        try {
            if (GOOGLE_ENHANCED_TYPES.includes(destinationCode)) {
                await googleEnhanced.handleCreate();
            } else if (destinationCode === Destination.imcapi) {
                await mappingOnly.saveImpactMapping();
            } else if (destinationCode === Destination.sstask) {
                await mappingOnly.saveSignalSightTaskMapping();
            } else if (tiktok.hasAccess) {
                await tiktok.handleCreate();
            } else if (destinationCode === Destination.licapi) {
                await (linkedin.isMappingOpen
                    ? linkedin.saveMappings()
                    : linkedin.create());
            } else if (destinationCode === Destination.msoffline) {
                await (microsoftBing.isMappingOpen
                    ? microsoftBing.saveMappings()
                    : microsoftBing.create());
            } else if (destinationCode === Destination.ymwc) {
                await yandexMetrica.create();
            } else if (googleAds.isConversionType) {
                await (googleAds.isMappingOpen
                    ? googleAds.saveMappings()
                    : googleAds.create());
            } else if (openAi.isMappingOpen) {
                await openAi.saveMappings();
            } else {
                await handleCreateDestination();
            }
        } finally {
            saveBusyRef.current = false;
        }
    };

    const closeLinkedinMapping = () => {
        linkedin.setIsMappingOpen(false);
        actions.dismiss();
        linkedin.isMappingCompleted && completeCreation(linkedin.destinationId);
    };

    const closeMicrosoftBingMapping = () => {
        microsoftBing.setIsMappingOpen(false);

        if (microsoftBing.isMappingCompleted) {
            actions.dismiss();
            completeCreation(microsoftBing.destinationId);
            return;
        }

        actions.refresh();
        onCancel();
    };

    const handleClose = () => {
        if (linkedin.isMappingOpen) {
            closeLinkedinMapping();
        } else if (microsoftBing.isMappingOpen) {
            closeMicrosoftBingMapping();
        } else if (googleAds.isMappingOpen) {
            googleAds.closeMapping();
        } else if (openAi.isMappingOpen) {
            openAi.closeMapping();
        } else {
            onCancel();
        }
    };

    return {
        language,
        appObject,
        isTrackerLoading,
        hostActions: actions,
        form,
        pinterest,
        facebook,
        reddit,
        tiktok,
        twitter,
        customAudience,
        googleEnhanced,
        linkedin,
        microsoftBing,
        yandexMetrica,
        googleAds,
        openAi,
        mappingOnly,
        oauthData,
        setOauthData,
        typeOptions,
        availableDestinationCodes,
        isTypeSelectLoading,
        isTypeLocked: isTypeLocked && !!initialDestinationType,
        isAnyMappingOpen,
        isSaveDisabled,
        setSaveDisabled,
        setIsSaveAvailable,
        isButtonLoader,
        setIsButtonLoader,
        setModalLoading,
        isFooterLoading: modalLoading || isButtonLoader,
        submitButtonRef,
        footerAction,
        registerFooterAction,
        completeCreation,
        handleTypeChange,
        resetFormState,
        handleSave,
        handleClose,
    };
};

export default useDestinationCreation;
